// /medical-council/$profession — TH detail page per profession
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MEDICAL_COUNCIL_PROFESSIONS, getMedicalCouncilProfession } from "@/data/medical-council-professions";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { ContactCTA } from "@/components/seo/Blocks";
import { Stethoscope, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, BadgeCheck, Globe2, ScrollText } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/medical-council/$profession")({
  loader: ({ params }) => {
    if (!getMedicalCouncilProfession(params.profession)) throw notFound();
    return { slug: params.profession };
  },
  head: ({ params }) => {
    const p = getMedicalCouncilProfession(params.profession);
    if (!p) return { meta: [{ title: ctaTitle("ไม่พบบริการ", "/medical-council/$profession", "th")}] };
    const url = `${SITE}/medical-council/${p.slug}`;
    const title = `รับรอง${p.councilTh} (${p.nameTh}) เริ่ม ฿${p.priceFromTh.toLocaleString()} เสร็จ ${p.turnaround} | NYC Legal`;
    const desc = `บริการรับรองเอกสารโดย${p.councilTh}สำหรับ${p.nameTh} พร้อมแปล Notary MFA และสถานทูต ใช้ได้ ${p.destinations.slice(0, 4).map((d) => d.nameTh).join(", ")} และอีกหลายประเทศ — ${p.councilEn} — สอบถามฟรี ไม่มีข้อผูกมัด`;
    return {
      meta: [
        { title: ctaTitle(title, "/medical-council/$profession", "th") }, { name: "description", content: ctaDesc(desc, "/medical-council/$profession", "th")}, { name: "keywords", content: p.keywordsTh.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/medical-council/$profession", "th")}, { property: "og:description", content: ctaDesc(desc, "/medical-council/$profession", "th")},
        { property: "og:url", content: url }, { property: "og:type", content: "article" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/medical-council/${p.slug}`, `/en/medical-council/${p.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${p.councilEn} Certification — ${p.nameEn}`,
              alternateName: `รับรอง${p.councilTh} ${p.nameTh}`,
              serviceType: "Professional Credential Verification",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              areaServed: { "@type": "Country", name: "Thailand" },
              offers: { "@type": "Offer", price: p.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `วิธีรับรองเอกสาร${p.councilTh}สำหรับ${p.nameTh}`,
              totalTime: "P15D",
              step: [
                { "@type": "HowToStep", position: 1, name: "ปรึกษาและประเมินเอกสาร", text: "ส่งสำเนาใบประกอบวิชาชีพและบัตรประชาชนทาง LINE @nycli ทีมประเมินภายใน 30 นาที" },
                { "@type": "HowToStep", position: 2, name: `ขอเอกสารจาก${p.councilTh}`, text: `ยื่นคำขอ Certificate of Good Standing ที่${p.councilTh} โดย NYC รับมอบอำนาจดำเนินการแทน` },
                { "@type": "HowToStep", position: 3, name: "แปลภาษาอังกฤษ", text: "แปลโดยนักแปลกระทรวงยุติธรรม (MoJ) หรือ NAATI พร้อมรับรองคำแปล" },
                { "@type": "HowToStep", position: 4, name: "Notary + MFA", text: "Notary Public รับรองลายมือชื่อ + รับรองกรมการกงสุล กระทรวงต่างประเทศ" },
                { "@type": "HowToStep", position: 5, name: "สถานทูตปลายทาง", text: "รับรองสถานทูตประเทศปลายทาง (ถ้าจำเป็น) แล้วจัดส่ง EMS/DHL ทั่วโลก" },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "รับรองแพทยสภา/สภาวิชาชีพ", item: `${SITE}/medical-council` },
                { "@type": "ListItem", position: 3, name: p.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
          faqPageSchema([
            { q: `รับรอง${p.councilTh}สำหรับ${p.nameTh} ราคาเท่าไหร่?`, a: `เริ่มต้น ฿${p.priceFromTh.toLocaleString()} (รวมแปล + Notary) ยังไม่รวมค่าธรรมเนียมสภา 200–1,500 บาทและค่ารับรอง MFA/สถานทูต ทีม NYC แจ้งราคาแบบครบจบก่อนเริ่มงานเสมอ` },
            { q: `${p.nameTh} ใช้เวลานานแค่ไหน?`, a: `งานปกติ ${p.turnaround} หากต้องรับรองกงสุล+สถานทูตปลายทางเพิ่มประมาณ 5–10 วันทำการ NYC ติดตามทุกขั้นตอนและแจ้งสถานะผ่าน LINE` },
            { q: `${p.councilTh}ออกเอกสารอะไรให้บ้าง?`, a: `${p.documents.join(", ")} — ทุกฉบับเป็นเอกสารทางการที่หน่วยงานต่างประเทศยอมรับเพื่อใช้ขึ้นทะเบียนหรือสอบใบประกอบวิชาชีพ` },
            { q: `ใช้ได้ที่ประเทศอะไรบ้าง?`, a: `รองรับประเทศปลายทางหลัก ได้แก่ ${p.destinations.map((d) => `${d.nameTh} (${d.authority})`).join(", ")} และประเทศอื่นตามที่ลูกค้าระบุ` },
            { q: `ต้องไปที่${p.councilTh}ด้วยตัวเองไหม?`, a: `ไม่ต้องครับ NYC รับมอบอำนาจดำเนินการแทน ลูกค้าเพียงเซ็นใบมอบอำนาจ + ส่งสำเนาบัตรประชาชนและใบประกอบวิชาชีพ — เราดำเนินการให้ครบทุกขั้นตอนและจัดส่งคืนทั่วประเทศและทั่วโลก` },
            { q: `รับงานด่วนได้ไหม?`, a: `ในบางสภาเช่น สภาการพยาบาลและสภาเภสัชกรรมมีบริการเร่งด่วน 3–5 วันทำการ (มีค่าธรรมเนียมเพิ่ม) แพทยสภาและทันตแพทยสภาเป็นไปตามคิวของสภา NYC ช่วยจองคิวให้เร็วที่สุด` },
          ]),
        ]),
      ],
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบบริการ</h1>
      <Link to="/medical-council" className="mt-4 inline-block text-gold underline">กลับ Medical Council hub</Link>
    </main>
  ),
});

function Page() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const p = getMedicalCouncilProfession(slug)!;
  const related = MEDICAL_COUNCIL_PROFESSIONS.filter((x) => x.slug !== slug).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/medical-council" className="hover:text-gold">แพทยสภา/สภาวิชาชีพ</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{p.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stethoscope className="h-3.5 w-3.5" /> {p.councilTh} · {p.councilEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            รับรองเอกสาร{p.councilTh}สำหรับ{p.nameTh}
          </h1>
          <p className="mt-2 text-base text-white/80">{p.nameEn} · {p.councilEn}</p>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการครบวงจรสำหรับ{p.nameTh}ไทยที่ต้องการนำใบประกอบวิชาชีพไปใช้ในต่างประเทศ —
            NYC ดำเนินการแทนตั้งแต่ขอ Certificate of Good Standing จาก{p.councilTh}
            แปลภาษาอังกฤษโดยนักแปล MoJ/NAATI Notary Public รับรองกงสุล MFA และสถานทูตปลายทาง
            จัดส่ง EMS/DHL ทั่วประเทศและทั่วโลก เริ่มต้นเพียง ฿{p.priceFromTh.toLocaleString()}
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${p.priceFromTh.toLocaleString()}` },
              { k: "TAT", v: p.turnaround },
              { k: "ประเทศปลายทาง", v: `${p.destinations.length}+` },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service ดูแลกระบวนการรับรอง{p.councilTh}สำหรับ{p.nameTh}แบบครบจบในที่เดียว
          เริ่ม ฿{p.priceFromTh.toLocaleString()} เสร็จ {p.turnaround} ครอบคลุมประเทศปลายทาง {p.destinations.map((d) => d.nameTh).join(", ")}
          พร้อมแปลภาษาอังกฤษโดยนักแปล MoJ/NAATI Notary Public กรมการกงสุล (MFA) และสถานทูตปลายทาง
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่{p.councilTh}ออกให้</h2>
          <p className="mt-2 text-sm text-slate-600">เลือกเอกสารที่ตรงกับวัตถุประสงค์การใช้งานของท่าน NYC จะช่วยประเมินและจัดทำใบมอบอำนาจให้ครบถ้วน</p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {p.documents.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศปลายทางและหน่วยงานรับรอง</h2>
          <p className="mt-2 text-sm text-slate-600">รายชื่อหน่วยงานต่างประเทศที่รับรองเอกสารจาก{p.councilTh}และ NYC ดูแลกระบวนการให้ทั้งหมด</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.destinations.map((d) => (
              <div key={d.code} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                  <Globe2 className="h-3 w-3" /> {d.code.toUpperCase()}
                </div>
                <div className="mt-1.5 font-display font-extrabold text-navy-deep">{d.nameTh}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{d.nameEn}</div>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                  <ShieldCheck className="h-3 w-3" /> {d.authority}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">การใช้งานหลัก</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {p.useCases.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ขั้นตอนเต็มรูปแบบ</div>
          <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วิธีรับรอง{p.councilTh}สำหรับ{p.nameTh} — 5 ขั้นตอน</h2>
          <ol className="mt-5 space-y-3">
            {[
              { t: "ปรึกษาและประเมินเอกสาร", d: "ส่งสำเนาใบประกอบวิชาชีพ + บัตรประชาชน + รายละเอียดประเทศปลายทาง ทีม NYC ประเมินค่าใช้จ่ายและระยะเวลาทั้งหมดภายใน 30 นาที" },
              { t: `ยื่นคำขอที่${p.councilTh}`, d: `เซ็นใบมอบอำนาจให้ NYC ดำเนินการแทน เราจัดทำคำขอ Certificate of Good Standing พร้อมเอกสารประกอบและชำระค่าธรรมเนียมที่${p.councilTh}แทนท่าน` },
              { t: "แปลภาษาอังกฤษ", d: "แปลโดยนักแปลกระทรวงยุติธรรม (MoJ) หรือ NAATI ออสเตรเลีย ขึ้นกับประเทศปลายทาง ราคาเริ่ม ฿1,300–2,500 ต่อหน้า" },
              { t: "Notary Public + MFA", d: "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ขึ้นทะเบียนสภาทนายความ รับรองคำแปล แล้วนำไปรับรองที่กรมการกงสุล กระทรวงการต่างประเทศ (MFA)" },
              { t: "สถานทูตปลายทาง + จัดส่ง", d: "ในกรณีประเทศปลายทางต้องการ NYC นำเอกสารไปรับรองสถานทูตแล้วจัดส่ง EMS (ในประเทศ ฿100) หรือ DHL/FedEx (ทั่วโลก ฿2,500)" },
            ].map((s, i) => (
              <li key={i} className="flex gap-4 rounded-xl border border-amber-200 bg-white p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg font-extrabold text-amber-700">{i + 1}</div>
                <div>
                  <div className="font-display font-extrabold text-navy-deep">{s.t}</div>
                  <div className="mt-1 text-[13px] leading-relaxed text-slate-700">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา (รวมแล้ว)</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            แพ็กเกจ{p.nameTh} เริ่ม ฿{p.priceFromTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เสร็จภายใน {p.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมแปล + Notary Public + ค่าดำเนินการที่{p.councilTh}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับรองกงสุล MFA เริ่ม ฿2,500–5,000 (เพิ่ม)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับรองสถานทูตปลายทาง ฿5,000–12,000 (ถ้าจำเป็น)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> EMS ในประเทศ ฿100 / DHL ทั่วโลก ฿2,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ผ่อนชำระและออกใบกำกับภาษีได้</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วิชาชีพอื่นที่ NYC ดูแลให้</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/medical-council/$profession" params={{ profession: r.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                  <ScrollText className="h-3 w-3" /> {r.councilTh}
                </div>
                <div className="mt-1.5 font-display font-extrabold text-navy-deep group-hover:text-amber-700">{r.nameTh}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{r.priceFromTh.toLocaleString()} · {r.turnaround}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">แหล่งอ้างอิงทางการ</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[13px] text-slate-700">
            <li><a href={p.councilUrl} target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">{p.councilTh} ({p.councilEn})</a></li>
            <li><a href="https://www.consular.mfa.go.th" target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">กรมการกงสุล กระทรวงการต่างประเทศ (MFA)</a></li>
            <li><a href="https://www.lawyerscouncil.or.th" target="_blank" rel="noopener noreferrer nofollow" className="text-amber-700 underline-offset-2 hover:underline">สภาทนายความแห่งประเทศไทย (Notary Public)</a></li>
          </ul>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
